import {
  Box,
  EmptyState,
  FlexBox,
  Modal,
  ModalHead,
  Text,
} from '@dixa/lib-shared-habla';
import { useModal } from 'lib-knowledge-modal';
import { KnowledgeLoading } from 'lib-knowledge-utils/components/loading';

import { useSuggestionComparison } from '../hooks/useSuggestionComparison';
import { Suggestion } from '../utils/toSuggestion';

import s from './articleComparisonModal.module.scss';

type ArticleComparisonModalProps = {
  suggestion: Suggestion;
};

/**
 * An update draft beside the article it would replace.
 *
 * The draft leads on the left, as version history also orders its panels: the
 * reviewer is judging the proposal, and the article as it stands is the
 * reference they check it against.
 *
 * Built on habla's `Modal` rather than `ModalWrapper`. The wrapper's only
 * addition is `width: 600px` and the two panels need far more, so going
 * straight to `Modal` — which sets no width of its own — makes the width here a
 * plain declaration instead of a fight over specificity. `ModalBody` is skipped
 * for the same reason: its only style is a `spacer-500` margin, and the panels
 * are meant to reach the modal's edges.
 */
export function ArticleComparisonModal({
  suggestion,
}: ArticleComparisonModalProps): JSX.Element {
  const { hideModal } = useModal();
  const { newMarked, oldMarked, loading, unavailable } =
    useSuggestionComparison(suggestion);

  return (
    <Modal isOpen onClose={hideModal} className={s.modal}>
      {/* `title` takes a node, so centring it needs no reach into ModalHead's
          own styles. It centres within the title column, which stops short of
          the close button — a hair left of the modal's true centre. */}
      <ModalHead
        title={<Box align="center">Article Comparison</Box>}
        onClose={hideModal}
      />

      <FlexBox flexDirection="column" className={s.body}>
        {loading && <KnowledgeLoading />}

        {!loading && unavailable && (
          <EmptyState
            size="small"
            icon="exclamation-triangle-filled"
            title="Nothing to compare against"
            description="The article this draft updates could not be loaded. It may have been deleted since the draft was written."
          />
        )}

        {!loading && !unavailable && (
          <>
            {/* Outside the scroller, so the columns stay labelled however far
                down the articles the reviewer has read. */}
            <FlexBox flex="0 0 auto" className={s.labels}>
              <Text.Paragraph variant="4" subdued width="50%" padding="300 400">
                Updated Version
              </Text.Paragraph>
              <Text.Paragraph variant="4" subdued width="50%" padding="300 400">
                Previous Version
              </Text.Paragraph>
            </FlexBox>

            <Box flex="1" minHeight="0" className={s.scroll}>
              <FlexBox
                flexDirection="row"
                alignItems="stretch"
                minHeight="100%"
                className={s.panels}
              >
                <div className={s.panel}>
                  {/* Both sides are generated markup — the draft's from the AI,
                      the article's from `serializeToHtml`. Rendered the same way
                      version history renders its own diff panels. */}
                  <div
                    className={s.panelArticle}
                    dangerouslySetInnerHTML={{ __html: newMarked }}
                  />
                </div>

                <div className={s.panel}>
                  <div
                    className={s.panelArticle}
                    dangerouslySetInnerHTML={{ __html: oldMarked }}
                  />
                </div>
              </FlexBox>
            </Box>
          </>
        )}
      </FlexBox>
    </Modal>
  );
}
